/**
 * Front API는 사이트와 같은 호스트의 `/api` 를 사용합니다.
 * - 로컬: Vite가 munwon07.cafe24builder.com 으로 프록시
 * - 배포(5b2e.co.kr 등 Builder 도메인): 같은 출처 `/api`
 */
export const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined)?.replace(/\/+$/, '') || '/api';

/** 상대 경로 첨부/이미지 URL */
export function resolveMediaUrl(url: string | null | undefined): string {
  if (!url) return '';
  if (/^(https?:|mailto:|tel:|#)/i.test(url)) return url;
  try {
    return new URL(url, window.location.origin).toString();
  } catch {
    return url;
  }
}

export class ApiError extends Error {
  status: number;
  body: unknown;

  constructor(message: string, status: number, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

export type Paginated<T> = {
  items: T[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
};

export type Board = {
  id: number;
  slug: string;
  type: string;
  name: string;
  description: string | null;
  is_active: boolean;
  post_count: number;
};

export type PostListItem = {
  id: number;
  board_id: number;
  title: string;
  summary: string | null;
  created_at: string;
  is_pinned: boolean;
  is_secret: boolean;
  view_count: number;
  thumbnail_url: string | null;
};

export type PostFile = {
  id: number;
  original_name: string;
  url: string | null;
  mime_type: string;
};

export type PostDetail = PostListItem & {
  content: string;
  files: PostFile[];
};

export type InquiryField = {
  key: string;
  type: 'text' | 'email' | 'textarea' | 'tel' | 'select' | 'date' | 'number' | 'checkbox';
  label: string;
  required: boolean;
  options: string[] | null;
};

export type InquiryForm = {
  id: number;
  name: string;
  description: string | null;
  fields: InquiryField[];
  submit_button_label: string | null;
  is_active: boolean;
};

export type Term = {
  id: number;
  slot_key: string;
  category: string;
  title: string;
  content: string;
  requires_user_consent: boolean;
};

type QueryValue = string | number | boolean | Array<string | number> | undefined;

function buildUrl(path: string, query?: Record<string, QueryValue>): string {
  const url = new URL(`${API_BASE}${path}`, window.location.origin);
  if (query) {
    Object.entries(query).forEach(([key, value]) => {
      if (value === undefined) return;
      if (Array.isArray(value)) {
        value.forEach((item) => url.searchParams.append(key, String(item)));
        return;
      }
      url.searchParams.set(key, String(value));
    });
  }
  return url.toString();
}

async function parseError(res: Response): Promise<ApiError> {
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    body = await res.text().catch(() => undefined);
  }
  const message =
    typeof body === 'object' && body && 'message' in body
      ? String((body as { message: unknown }).message)
      : `API 오류 (${res.status})`;
  return new ApiError(message, res.status, body);
}

async function request<T>(path: string, init?: RequestInit, query?: Record<string, QueryValue>): Promise<T> {
  const res = await fetch(buildUrl(path, query), {
    ...init,
    headers: {
      Accept: 'application/json',
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
  });

  if (!res.ok) {
    throw await parseError(res);
  }

  if (res.status === 204) {
    return undefined as T;
  }

  return (await res.json()) as T;
}

export function apiGet<T>(path: string, query?: Record<string, QueryValue>): Promise<T> {
  return request<T>(path, { method: 'GET' }, query);
}

export function apiPost<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, { method: 'POST', body: JSON.stringify(body) });
}

let boardCache: Board[] | null = null;

export async function listBoards(): Promise<Board[]> {
  if (boardCache) return boardCache;
  const data = await apiGet<Paginated<Board>>('/front/v1/boards', { page_size: 100 });
  boardCache = data.items.filter((board) => board.is_active);
  return boardCache;
}

function normalizeName(value: string): string {
  return value.replace(/\s+/g, '').toLowerCase();
}

export async function findBoardByName(name: string): Promise<Board | null> {
  const boards = await listBoards();
  const exact = boards.find((board) => board.name === name);
  if (exact) return exact;
  const target = normalizeName(name);
  return boards.find((board) => normalizeName(board.name) === target) ?? null;
}

export async function listPosts(slug: string, page = 1, pageSize = 10): Promise<Paginated<PostListItem>> {
  return apiGet<Paginated<PostListItem>>(`/front/v1/boards/${encodeURIComponent(slug)}/posts`, {
    page,
    page_size: pageSize,
  });
}

export async function getPost(postId: number): Promise<PostDetail> {
  return apiGet<PostDetail>(`/front/v1/posts/${postId}`);
}

export async function listInquiryForms(): Promise<InquiryForm[]> {
  const data = await apiGet<Paginated<InquiryForm>>('/front/v1/inquiry-forms', { page_size: 100 });
  return data.items.filter((form) => form.is_active);
}

export async function findInquiryFormByName(name: string): Promise<InquiryForm | null> {
  const forms = await listInquiryForms();
  const exact = forms.find((form) => form.name === name);
  if (exact) return exact;
  const target = normalizeName(name);
  return forms.find((form) => normalizeName(form.name) === target) ?? null;
}

export async function getTerms(slotKeys: string[]): Promise<Term[]> {
  const data = await apiGet<Paginated<Term>>('/front/v1/terms', {
    slot_keys: slotKeys,
    page_size: 20,
  });
  return data.items;
}

export async function getTermBySlot(slotKey: string): Promise<Term | null> {
  const terms = await getTerms([slotKey]);
  return terms.find((term) => term.slot_key === slotKey) ?? terms[0] ?? null;
}

export type SubmitInquiryPayload = {
  form_id: number;
  name: string;
  phone?: string | null;
  email?: string | null;
  subject?: string | null;
  content?: string | null;
  form_data: Record<string, unknown>;
  agreements: Array<{ term_id: number; agreed: boolean }>;
};

export function submitInquiry(payload: SubmitInquiryPayload) {
  return apiPost('/front/v1/inquiries', payload);
}
